// Overview page components
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Card, CardHeader, Grid } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { Pagination, Spin, Typography } from "antd";
import { useEffect } from "react";
import CountUp from "react-countup";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "~/components/MDBox";
import DefaultProjectCard from "~/examples/Cards/ProjectCards/DefaultProjectCard";
import DashboardLayout from "~/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "~/examples/Navbars/DashboardNavbar";

import EmptyResult from "~/components/Extras/Empty";
import MDButton from "~/components/MDButton";
import StatisticCard from "~/examples/StaticsCard";
import usePagination from "~/hooks/usePagination";
import dashboardServices from "~/services/dashboard.services";
import { setClickedCardIndex } from "~/store/slices/utilities";
import { getImageUrl } from "~/utils/utils";

const formatter = (value) => <CountUp end={value} separator="," />;

const Plantations = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { locationState } = useSelector((store) => store.utils);

  const { data, isLoading, isSuccess, mutate, mutateAsync, reset } =
    useMutation({
      mutationKey: ["Plantations", location.state?.Plot_Auto_ID],
      mutationFn: (payloads) =>
        dashboardServices.getPlantDetailsBasedOnPlot(payloads),
    });

  const plotMutation = useMutation({
    mutationKey: ["PLOT", location.state?.Plot_Auto_ID],
    mutationFn: (payload) => dashboardServices.getPlotData(payload),
  });

  const { currentPage, handlePageChange, pageSize } = usePagination(
    mutate,
    location.state?.Plot_Auto_ID,
    6
  );

  useEffect(() => {
    mutate({ id: location.state?.Plot_Auto_ID, currentPage, pageSize });
    // plotMutation.mutate(location.state?.Plot_Auto_ID);
    return () => {
      reset();
    };
  }, []);
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        sx={{
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <MDBox>
          <CardHeader
            action={
              <MDButton
                variant="contained"
                color="success"
                aria-label="navigation"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
              >
                Back
              </MDButton>
            }
            title="Plantations"
            // subheader={`Overview of ${location.state?.Loc_Name}/${plotMutation.data?.data?.Plot_Code}/${plotMutation.data?.data?.Plot_Name}`}
          />
          {/* <MDTypography variant="h5">Create Plant</MDTypography> */}
        </MDBox>

        <MDBox p={2}>
          <Spin tip="Loading..." spinning={isLoading}>
            <Grid container spacing={6}>
              {data?.data?.length === 0 && isSuccess ? (
                <Grid item xs={12} md={12} xl={12} lg={12}>
                  <EmptyResult
                    description="No data available. Click below to create new Plantation"
                    navigateTo="/plantation-master/create"
                  />
                </Grid>
              ) : null}
              {data?.data?.map((item) => (
                <Grid item xs={12} md={6} xl={4} lg={3} key={item?.Created_Dt}>
                  <Card
                    sx={{ cursor: "pointer", p: 1 }}
                    onClick={() => {
                      dispatch(
                        setClickedCardIndex({
                          route: "plantation",
                          index: item?.Plantation_Auto_ID,
                        })
                      );
                      navigate(
                        `/dashboard/location/plots/plantation/${item.plant_mstr.Plant_Local_Name}`,
                        {
                          state: {
                            name: item.plant_mstr.Plant_Local_Name,
                            ...item,
                          },
                        }
                      );
                    }}
                  >
                    <DefaultProjectCard
                      route="plantation"
                      index={item?.Plantation_Auto_ID}
                      image={getImageUrl(item.plant_mstr.Plant_Img)}
                      label={item.plant_mstr.Plant_Scientific_Name}
                      title={item.plant_mstr.Plant_Local_Name}
                      plantColor={item.plant_mstr.Plant_Color}
                      lat={item?.Latitude}
                      long={item?.Longitude}
                      plantationData={item?.plantationdtl}
                      staticsData={item?.plantCount}
                      description={
                        <StatisticCard
                          title1="Number of Plants"
                          title2="Total Monitoring"
                          statics1={item?.Total_Plant_Count}
                          statics2={item?.Total_Plant_Monitoring_Count}
                          formatter={formatter}
                          /* new changes - tag v3.0.1 */
                          description={[
                            {
                              key: 1,
                              label: (
                                <Typography.Text strong>Aspect</Typography.Text>
                              ),
                              children: item.Aspect,
                            },
                            {
                              key: 2,
                              label: (
                                <Typography.Text strong>
                                  Soil Texture
                                </Typography.Text>
                              ),
                              children: item.Soil_Texture,
                            },
                          ]}
                        />
                      }
                      action={{
                        type: "internal",
                        //   route: `/dashboard/location/plotting/plantation/plant/${item.Plant_Auto_ID}`,
                        color: "success",
                        label: "View Monitoring",
                      }}
                      // authors={[
                      //   { image: team1, name: 'Elena Morison' },
                      //   { image: team2, name: 'Ryan Milly' },
                      //   { image: team3, name: 'Nick Daniel' },
                      //   { image: team4, name: 'Peterson' },
                      // ]}
                    />
                  </Card>
                </Grid>
              ))}
              <Grid
                item
                lg={12}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <Pagination
                  defaultPageSize={6}
                  defaultCurrent={currentPage}
                  total={data?.count ?? 10}
                  onChange={handlePageChange}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                />
              </Grid>
            </Grid>
          </Spin>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default Plantations;
